/**
 * ProjectsMap Component
 *
 * Displays projects on an interactive map using OpenMapTiles.
 * Uses MapLibre GL JS for rendering.
 */

import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import brandConfig from '../../config/brandConfig';

// Add custom styles for markers
const mapStyles = `
    .maplibregl-marker {
        cursor: pointer !important;
        pointer-events: auto !important;
    }
    .maplibregl-popup {
        pointer-events: none !important;
    }
    .maplibregl-popup-content {
        pointer-events: auto !important;
        cursor: default !important;
    }
    .project-marker {
        pointer-events: auto !important;
        cursor: pointer !important;
    }
    .project-popup .maplibregl-popup-content {
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    }
    .project-popup .maplibregl-popup-tip {
        display: block;
    }
`;

// Thunderforest Cycle Map configuration
// Using Thunderforest Cycle Map tiles (same as OpenStreetMap website)
// Thunderforest Cycle Map tile URL with API key
const THUNDERFOREST_CYCLE_MAP_TILES = `https://tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=${brandConfig.thunderforestApiKey}`;

function ProjectsMap({
    projects,
    hoveredProjectId,
    selectedProjectId,
    onHover,
    onSelect,
    countryConfig,
    countryCode,
    projectsBbox
}) {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markersRef = useRef({});
    const [mapLoaded, setMapLoaded] = useState(false);

    // Initialize map
    useEffect(() => {
        if (map.current || !mapContainer.current) {
            return;
        }

        // Use Thunderforest Cycle Map tiles (raster tiles)
        // Using the tile.thunderforest.com endpoint with API key
        const style = {
            version: 8,
            sources: {
                'thunderforest-cycle': {
                    type: 'raster',
                    tiles: [THUNDERFOREST_CYCLE_MAP_TILES],
                    tileSize: 256,
                    attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors. Tiles courtesy of <a href="http://www.thunderforest.com/" target="_blank">Andy Allan</a>',
                    maxzoom: 21
                }
            },
            layers: [
                {
                    id: 'thunderforest-cycle-layer',
                    type: 'raster',
                    source: 'thunderforest-cycle',
                    minzoom: 0,
                    maxzoom: 21
                }
            ]
        };

        // Declare resizeObserver outside try block so it's available in cleanup
        let resizeObserver = null;

        // countryConfig.defaultCenter is stored as [lat, lng] but MapLibre expects [lng, lat]
        const configCenter = countryConfig?.defaultCenter;
        const center = Array.isArray(configCenter) && configCenter.length === 2
            ? [configCenter[1], configCenter[0]]
            : [0, 0];

        try {
            map.current = new maplibregl.Map({
                container: mapContainer.current,
                style: style,
                center,
                zoom: countryConfig?.defaultZoom || 2,
                maxZoom: 10,
                antialias: true
            });

            map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

            map.current.on('load', () => {
                // Force resize to ensure map renders correctly
                setTimeout(() => {
                    if (map.current) {
                        map.current.resize();
                        // Force a repaint
                        map.current.triggerRepaint();
                    }
                }, 200);
                setMapLoaded(true);
            });

            map.current.on('error', (e) => {
                console.error('❌ Map error:', e);
            });

            map.current.on('style.load', () => {
                // Resize after style loads
                setTimeout(() => {
                    if (map.current) {
                        map.current.resize();
                    }
                }, 100);
            });

            // Resize when container dimensions change
            if (window.ResizeObserver && mapContainer.current) {
                resizeObserver = new ResizeObserver(() => {
                    if (map.current) {
                        map.current.resize();
                    }
                });
                resizeObserver.observe(mapContainer.current);
            }

        } catch (error) {
            console.error('❌ Error creating map:', error);
        }

        return () => {
            if (resizeObserver && mapContainer.current) {
                resizeObserver.unobserve(mapContainer.current);
                resizeObserver.disconnect();
            }
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [countryConfig]);

    // Update markers when projects change
    useEffect(() => {
        if (!map.current || !mapLoaded) return;

        // Remove existing markers
        Object.values(markersRef.current).forEach((markerData) => {
            if (markerData && markerData.marker) {
                markerData.marker.remove();
            }
        });
        markersRef.current = {};

        // Add markers for each project
        const bounds = new maplibregl.LngLatBounds();
        let hasValidCoordinates = false;

        projects.forEach(project => {
            if (!project.center_lng || !project.center_lat) {
                return;
            }

            const lng = parseFloat(project.center_lng);
            const lat = parseFloat(project.center_lat);
            
            if (isNaN(lng) || isNaN(lat)) {
                return;
            }

            const coordinates = [lng, lat];

            // Create marker container (this is what MapLibre will position)
            const markerContainer = document.createElement('div');
            markerContainer.className = 'project-marker-container';

            // Create marker element inside container
            const markerEl = document.createElement('div');
            markerEl.className = 'project-marker';
            markerEl.setAttribute('data-project-id', project.id);
            markerEl.style.cssText = `
                width: 40px;
                height: 40px;
                background: linear-gradient(135deg, #E8751A 0%, #FFBF00 100%);
                border: 3px solid white;
                border-radius: 50%;
                box-shadow: 0 4px 12px rgba(232, 117, 26, 0.4);
                cursor: pointer;
                transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                transform: scale(1);
            `;

            // Add icon inside marker
            const iconEl = document.createElement('div');
            iconEl.style.pointerEvents = 'none';
            iconEl.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
            markerEl.appendChild(iconEl);

            // Append marker to container
            markerContainer.appendChild(markerEl);

            // Create popup FIRST
            const locationText = project.location || project.district || project.province || 'Ubicación no especificada';
            const popupId = `popup-${project.id}`;
            const popup = new maplibregl.Popup({
                offset: 30,
                closeButton: false,
                closeOnClick: false,
                maxWidth: '300px',
                className: 'project-popup'
            }).setHTML(`
                <div id="${popupId}" style="padding: 14px; font-family: system-ui, -apple-system, sans-serif; pointer-events: auto; cursor: pointer;" data-project-slug="${project.slug}" data-project-id="${project.id}">
                    <h3 style="font-weight: 700; font-size: 16px; margin-bottom: 8px; color: #1f2937;">${project.name}</h3>
                    <div style="display: flex; align-items: center; margin-bottom: 8px; color: #6b7280; font-size: 13px;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="margin-right: 6px; flex-shrink: 0;">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        ${locationText}
                    </div>
                    <div style="display: inline-block; padding: 5px 12px; background: #dcfce7; color: #15803d; border-radius: 14px; font-size: 12px; font-weight: 600; margin-bottom: 10px;">
                        <span style="display: inline-block; width: 6px; height: 6px; background: #15803d; border-radius: 50%; margin-right: 6px;"></span>
                        ${project.total_lots || 0} lotes disponibles
                    </div>
                    <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
                        <div style="background: linear-gradient(135deg, #E8751A 0%, #FFBF00 100%); color: white; padding: 8px 16px; border-radius: 8px; text-align: center; font-size: 13px; font-weight: 600; cursor: pointer; transition: transform 0.2s ease;" onmouseover="this.style.transform='scale(1.02)'" onmouseout="this.style.transform='scale(1)'">
                            Ver detalles del proyecto →
                        </div>
                    </div>
                </div>
            `);

            // Create marker with the container and attach popup
            const marker = new maplibregl.Marker({
                element: markerContainer
            })
                .setLngLat(coordinates)
                .setPopup(popup)
                .addTo(map.current);

            // Hover to show/hide popup - NO navigation on marker click
            let popupTimeout;

            const showPopup = () => {
                clearTimeout(popupTimeout);
                onHover(project.id);
                if (!marker.getPopup().isOpen()) {
                    marker.togglePopup();

                    // Add event listeners to popup after it's opened
                    setTimeout(() => {
                        const popupElement = document.getElementById(popupId);
                        if (popupElement) {
                            // Click on popup navigates to project
                            popupElement.addEventListener('click', (e) => {
                                e.stopPropagation();
                                if (project.slug) {
                                    window.location.href = `/proyectos/${project.slug}`;
                                } else {
                                    window.location.href = `/proyectos/${project.id}`;
                                }
                            });

                            // Keep popup open when hovering over it
                            popupElement.addEventListener('mouseenter', () => {
                                clearTimeout(popupTimeout);
                            });

                            // Close popup when leaving it
                            popupElement.addEventListener('mouseleave', () => {
                                hidePopup();
                            });
                        }
                    }, 50);
                }
            };

            const hidePopup = () => {
                popupTimeout = setTimeout(() => {
                    onHover(null);
                    if (marker.getPopup().isOpen()) {
                        marker.togglePopup();
                    }
                }, 200);
            };

            // Show popup on marker hover
            markerEl.addEventListener('mouseenter', showPopup);
            markerEl.addEventListener('mouseleave', hidePopup);

            // Store marker reference
            markersRef.current[project.id] = {
                marker,
                element: markerEl,
                popup
            };

            // Extend bounds
            bounds.extend(coordinates);
            hasValidCoordinates = true;
        });

        // Fit map to bounds
        if (hasValidCoordinates && !projectsBbox) {
            if (projects.length > 1) {
                map.current.fitBounds(bounds, {
                    padding: { top: 50, bottom: 50, left: 50, right: 50 },
                    maxZoom: 10,
                    duration: 1000
                });
            } else if (projects.length === 1) {
                map.current.flyTo({
                    center: [parseFloat(projects[0].center_lng), parseFloat(projects[0].center_lat)],
                    zoom: 10,
                    duration: 1000
                });
            }
        }

    }, [projects, mapLoaded, onHover, onSelect, projectsBbox]);

    // Apply bbox general when available
    useEffect(() => {
        if (!map.current || !mapLoaded || !projectsBbox || projects.length === 0) return;

        const applyBbox = () => {
            if (!map.current || !projectsBbox) return;

            try {
                const bboxBounds = new maplibregl.LngLatBounds(
                    [projectsBbox.minLng, projectsBbox.minLat],
                    [projectsBbox.maxLng, projectsBbox.maxLat]
                );

                if (bboxBounds.isEmpty()) {
                    return;
                }

                setTimeout(() => {
                    if (map.current) {
                        map.current.fitBounds(bboxBounds, {
                            padding: { top: 50, bottom: 50, left: 50, right: 50 },
                            maxZoom: 10,
                            duration: 1000
                        });
                    }
                }, 200);
            } catch (error) {
                console.error('Error applying bbox:', error);
            }
        };

        if (map.current.isStyleLoaded()) {
            applyBbox();
        } else {
            map.current.once('style.load', applyBbox);
        }
    }, [projectsBbox, mapLoaded, projects.length]);

    // Update marker styles based on hover/selection and zoom to selected project
    useEffect(() => {
        Object.entries(markersRef.current).forEach(([projectId, { element }]) => {
            const isHovered = hoveredProjectId === projectId;
            const isSelected = selectedProjectId === projectId;

            if (!element) return;

            // Update only the inner marker element's visual styles
            // Don't touch the container that MapLibre positions
            if (isSelected) {
                element.style.background = 'linear-gradient(135deg, #10b981 0%, #34d399 100%)';
                element.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.6)';
                element.style.width = '48px';
                element.style.height = '48px';
            } else if (isHovered) {
                element.style.background = 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)';
                element.style.boxShadow = '0 6px 18px rgba(245, 158, 11, 0.5)';
                element.style.width = '44px';
                element.style.height = '44px';
            } else {
                element.style.background = 'linear-gradient(135deg, #E8751A 0%, #FFBF00 100%)';
                element.style.boxShadow = '0 4px 12px rgba(232, 117, 26, 0.4)';
                element.style.width = '40px';
                element.style.height = '40px';
            }
        });

        // Zoom to selected project
        if (selectedProjectId && map.current && mapLoaded) {
            const selectedProject = projects.find(p => p.id === selectedProjectId);
            if (selectedProject && selectedProject.center_lng && selectedProject.center_lat) {
                const lng = parseFloat(selectedProject.center_lng);
                const lat = parseFloat(selectedProject.center_lat);
                
                if (!isNaN(lng) && !isNaN(lat)) {
                    map.current.flyTo({
                        center: [lng, lat],
                        zoom: 14,
                        duration: 1000
                    });
                }
            }
        }
    }, [hoveredProjectId, selectedProjectId, mapLoaded, projects]);

    return (
        <div className="relative w-full h-full" style={{ height: '100%', width: '100%', position: 'relative', minHeight: '300px' }}>
            {/* Inject custom styles */}
            <style>{mapStyles}</style>

            <div
                ref={mapContainer}
                className="w-full h-full"
                style={{
                    height: '100%',
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                }}
            />

            {/* Loading overlay - only show if map is not loaded */}
            {!mapLoaded && (
                <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center z-50">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-400">Cargando mapa...</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProjectsMap;
